import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';
import type {
  AdminMainDomainStatsResponse,
  MainDomainDailyPoint,
  MainDomainPageRow,
} from '@/lib/types';

const RANGE_OPTIONS = [
  { days: '7', label: '7d' },
  { days: '14', label: '14d' },
  { days: '30', label: '30d' },
  { days: '90', label: '90d' },
] as const;

type RangeDays = (typeof RANGE_OPTIONS)[number]['days'];
const ALLOWED_DAYS = new Set<string>(
  RANGE_OPTIONS.map((option) => option.days)
);
type PageSortKey =
  | 'host'
  | 'path'
  | 'visits'
  | 'unique_visitors'
  | 'cta_clicks'
  | 'cta_rate';

function formatNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString();
}

function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
        {label}
      </p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
        {value}
      </p>
      <p className='mt-1 text-xs text-gray-500'>{hint}</p>
    </div>
  );
}

const TREND_SERIES = [
  { key: 'visits', label: 'Visits', color: '#0ea5e9', swatch: 'bg-sky-500' },
  {
    key: 'unique_visitors',
    label: 'Unique visitors',
    color: '#14b8a6',
    swatch: 'bg-teal-500',
  },
  {
    key: 'cta_clicks',
    label: 'CTA clicks',
    color: '#fbbf24',
    swatch: 'bg-amber-400',
  },
] as const;

function niceAxisMax(value: number): number {
  if (value <= 0) return 5;
  if (value <= 5) return 5;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function yAxisTicks(max: number): number[] {
  const top = niceAxisMax(max);
  const step =
    top <= 5
      ? 1
      : top <= 10
      ? 2
      : top <= 25
      ? 5
      : top <= 50
      ? 10
      : Math.ceil(top / 4 / 10) * 10;
  const ticks: number[] = [];
  for (let value = 0; value <= top; value += step) ticks.push(value);
  if (ticks[ticks.length - 1] !== top) ticks.push(top);
  return ticks;
}

function formatDateLabel(ymd: string): string {
  const [year, month, day] = ymd.split('-').map(Number);
  if (!year || !month || !day) return ymd;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(
    undefined,
    {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }
  );
}

function formatAxisDate(ymd: string, showYear: boolean): string {
  const [year, month, day] = ymd.split('-').map(Number);
  if (!year || !month || !day) return ymd;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(
    undefined,
    {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      ...(showYear ? { year: '2-digit' } : {}),
    }
  );
}

function xLabelIndices(count: number): number[] {
  if (count <= 1) return [0];
  if (count <= 8) return Array.from({ length: count }, (_, index) => index);
  const targetLabels = count <= 31 ? 7 : 10;
  const step = Math.max(1, Math.round((count - 1) / (targetLabels - 1)));
  const indices: number[] = [0];
  for (let index = step; index < count - 1; index += step) indices.push(index);
  if (indices[indices.length - 1] !== count - 1) indices.push(count - 1);
  return indices;
}

function TrendChart({ data }: { data: MainDomainDailyPoint[] }) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [hovered, setHovered] = useState<{
    dayIndex: number;
    seriesIndex: number | null;
  } | null>(null);

  useEffect(() => {
    const element = chartRef.current;
    if (!element) return;
    const updateWidth = () => {
      const next = element.clientWidth;
      if (next > 0) setChartWidth(next);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (!data.length || chartWidth <= 0) return null;
    const dayCount = data.length;
    const padLeft = 44;
    const padRight = 12;
    const padTop = 16;
    const padBottom = 28;
    const height = 220;
    const innerW = chartWidth - padLeft - padRight;
    const innerH = height - padTop - padBottom;
    const slotW = innerW / dayCount;
    const groupGap = Math.min(6, Math.max(1, slotW * 0.18));
    const barGap = dayCount <= 14 ? 2 : 1;
    const barW = Math.max(
      2,
      (slotW - groupGap - barGap * (TREND_SERIES.length - 1)) /
        TREND_SERIES.length
    );
    const maxValue = Math.max(
      0,
      ...data.map((point) =>
        Math.max(point.visits, point.unique_visitors, point.cta_clicks)
      )
    );
    const axisMax = niceAxisMax(maxValue);
    const firstYear = data[0].date.slice(0, 4);
    const lastYear = data[dayCount - 1].date.slice(0, 4);
    return {
      dayCount,
      width: chartWidth,
      height,
      padLeft,
      padRight,
      padTop,
      padBottom,
      innerW,
      innerH,
      slotW,
      groupGap,
      barGap,
      barW,
      axisMax,
      ticks: yAxisTicks(maxValue),
      labelIndices: new Set(xLabelIndices(dayCount)),
      showYearOnAxis: firstYear !== lastYear,
    };
  }, [chartWidth, data]);

  if (!data.length) {
    return <p className='text-sm text-gray-500'>No traffic in this range.</p>;
  }

  const hoveredPoint = hovered && layout ? data[hovered.dayIndex] : null;
  const hoveredBar =
    hovered && layout && hoveredPoint
      ? (() => {
          const seriesIndex = hovered.seriesIndex;
          const value =
            seriesIndex !== null
              ? hoveredPoint[TREND_SERIES[seriesIndex].key]
              : Math.max(
                  hoveredPoint.visits,
                  hoveredPoint.unique_visitors,
                  hoveredPoint.cta_clicks
                );
          const barHeight =
            layout.axisMax > 0 ? (value / layout.axisMax) * layout.innerH : 0;
          if (seriesIndex !== null) {
            const groupX =
              layout.padLeft +
              hovered.dayIndex * layout.slotW +
              layout.groupGap / 2;
            const x = groupX + seriesIndex * (layout.barW + layout.barGap);
            return {
              cx: x + layout.barW / 2,
              y: layout.height - layout.padBottom - barHeight,
            };
          }
          return {
            cx:
              layout.padLeft +
              hovered.dayIndex * layout.slotW +
              layout.slotW / 2,
            y: layout.height - layout.padBottom - barHeight,
          };
        })()
      : null;

  return (
    <div className='space-y-1'>
      <div className='mb-2 flex flex-wrap items-center gap-4 text-xs text-gray-500'>
        {TREND_SERIES.map((series) => (
          <span key={series.key} className='inline-flex items-center gap-1'>
            <span className={`h-2 w-2 rounded-sm ${series.swatch}`} />{' '}
            {series.label}
          </span>
        ))}
      </div>
      <div
        ref={chartRef}
        className='rounded-md border border-gray-100 bg-gray-50/50'
        onMouseLeave={() => setHovered(null)}
      >
        {layout ? (
          <svg
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            width='100%'
            height={layout.height}
            role='img'
            aria-label='Traffic trend bar chart'
          >
            {layout.ticks.map((tick) => {
              const y =
                layout.height -
                layout.padBottom -
                (tick / layout.axisMax) * layout.innerH;
              return (
                <g key={tick}>
                  <line
                    x1={layout.padLeft}
                    y1={y}
                    x2={layout.padLeft + layout.innerW}
                    y2={y}
                    stroke={tick === 0 ? '#d1d5db' : '#e5e7eb'}
                    strokeDasharray={tick === 0 ? undefined : '4 4'}
                  />
                  <text
                    x={layout.padLeft - 8}
                    y={y + 4}
                    textAnchor='end'
                    className='fill-gray-500 text-[10px] tabular-nums'
                  >
                    {tick.toLocaleString()}
                  </text>
                </g>
              );
            })}

            {data.map((point, dayIndex) => {
              const groupX =
                layout.padLeft + dayIndex * layout.slotW + layout.groupGap / 2;
              const isDayHovered = hovered?.dayIndex === dayIndex;
              const showXLabel = layout.labelIndices.has(dayIndex);
              return (
                <g key={point.date}>
                  <rect
                    x={layout.padLeft + dayIndex * layout.slotW}
                    y={layout.padTop}
                    width={layout.slotW}
                    height={layout.innerH}
                    fill='transparent'
                    className='cursor-pointer'
                    onMouseEnter={() =>
                      setHovered({ dayIndex, seriesIndex: null })
                    }
                  />
                  {TREND_SERIES.map((series, seriesIndex) => {
                    const value = point[series.key];
                    const barHeight =
                      layout.axisMax > 0
                        ? (value / layout.axisMax) * layout.innerH
                        : 0;
                    const x =
                      groupX + seriesIndex * (layout.barW + layout.barGap);
                    const y = layout.height - layout.padBottom - barHeight;
                    const isBarHovered =
                      isDayHovered && hovered?.seriesIndex === seriesIndex;
                    const dimmed =
                      hovered !== null &&
                      !(isDayHovered && hovered.seriesIndex === null) &&
                      !isBarHovered;
                    const hitHeight = Math.max(barHeight, 12);
                    return (
                      <g key={series.key}>
                        <rect
                          x={x}
                          y={y}
                          width={layout.barW}
                          height={Math.max(0, barHeight)}
                          fill={series.color}
                          fillOpacity={isBarHovered ? 1 : dimmed ? 0.45 : 0.9}
                          rx={2}
                          className='pointer-events-none'
                        />
                        <rect
                          x={x}
                          y={layout.height - layout.padBottom - hitHeight}
                          width={layout.barW}
                          height={hitHeight}
                          fill='transparent'
                          className='cursor-pointer'
                          onMouseEnter={() =>
                            setHovered({ dayIndex, seriesIndex })
                          }
                          onMouseLeave={() =>
                            setHovered({ dayIndex, seriesIndex: null })
                          }
                        />
                      </g>
                    );
                  })}
                  {showXLabel ? (
                    <text
                      x={
                        layout.padLeft +
                        dayIndex * layout.slotW +
                        layout.slotW / 2
                      }
                      y={layout.height - layout.padBottom + 14}
                      textAnchor='middle'
                      className='fill-gray-500 text-[10px] pointer-events-none'
                    >
                      {formatAxisDate(point.date, layout.showYearOnAxis)}
                    </text>
                  ) : null}
                </g>
              );
            })}

            {hoveredPoint && hoveredBar ? (
              <TrendHoverTooltip
                point={hoveredPoint}
                seriesIndex={hovered?.seriesIndex ?? null}
                x={hoveredBar.cx}
                barTopY={hoveredBar.y}
                minX={layout.padLeft}
                maxX={layout.padLeft + layout.innerW}
                minY={layout.padTop}
                maxY={layout.height - layout.padBottom}
              />
            ) : null}
          </svg>
        ) : (
          <div className='h-[220px]' />
        )}
      </div>
    </div>
  );
}

function TrendHoverTooltip({
  point,
  seriesIndex,
  x,
  barTopY,
  minX,
  maxX,
  minY,
  maxY,
}: {
  point: MainDomainDailyPoint;
  seriesIndex: number | null;
  x: number;
  barTopY: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}) {
  const lines = [
    formatDateLabel(point.date),
    ...TREND_SERIES.map(
      (series) => `${series.label}: ${point[series.key].toLocaleString()}`
    ),
  ];
  const tooltipW = Math.max(
    148,
    ...lines.map((line) => line.length * 6.4 + 20)
  );
  const tooltipH = 16 + lines.length * 14;
  const tooltipX = Math.min(Math.max(x - tooltipW / 2, minX), maxX - tooltipW);
  const aboveY = barTopY - tooltipH - 8;
  const tooltipY =
    aboveY >= minY ? aboveY : Math.min(barTopY + 10, maxY - tooltipH);

  return (
    <g pointerEvents='none'>
      <rect
        x={tooltipX}
        y={tooltipY}
        width={tooltipW}
        height={tooltipH}
        rx={6}
        fill='#111827'
        opacity={0.92}
      />
      {lines.map((line, index) => {
        const isSeriesLine = index > 0;
        const isHoveredSeries =
          isSeriesLine && seriesIndex !== null && index - 1 === seriesIndex;
        return (
          <text
            key={line}
            x={tooltipX + 10}
            y={tooltipY + 16 + index * 14}
            className={`text-[11px] ${
              index === 0
                ? 'fill-white font-semibold'
                : isHoveredSeries
                ? 'fill-white font-semibold tabular-nums'
                : 'fill-gray-200 tabular-nums'
            }`}
          >
            {line}
          </text>
        );
      })}
    </g>
  );
}

function exportCsv(rows: MainDomainPageRow[], days: number) {
  const header = [
    'domain',
    'path',
    'visits',
    'unique_visitors',
    'cta_clicks',
    'cta_rate',
  ];
  const body = rows.map((row) => [
    row.host,
    row.path,
    String(row.visits),
    String(row.unique_visitors),
    String(row.cta_clicks),
    row.cta_rate.toFixed(4),
  ]);
  const csv = [header, ...body]
    .map((line) =>
      line.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')
    )
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `main-domains-${days}d.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
  track('Main Domains CSV Exported', { days, rows: rows.length });
}

function AdminMainDomainsPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate({ from: '/admin/main-domains' });
  const { days, host } = Route.useSearch();
  const daysNum = Number(days);
  const [sortKey, setSortKey] = useState<PageSortKey>('visits');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    track('Main Domains Admin Screen');
  }, []);

  const statsQuery = useQuery({
    queryKey: ['admin', 'main-domains', daysNum, host ?? 'all'],
    queryFn: () =>
      apiJson<AdminMainDomainStatsResponse>(
        getToken,
        `/v1/admin/main-domains/stats?days=${daysNum}${
          host ? `&host=${encodeURIComponent(host)}` : ''
        }`
      ),
    staleTime: 15 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const data = statsQuery.data;
  const sortedPages = useMemo(() => {
    const rows = [...(data?.pages ?? [])];
    rows.sort((left, right) => {
      const result =
        sortKey === 'host' || sortKey === 'path'
          ? left[sortKey].localeCompare(right[sortKey])
          : left[sortKey] - right[sortKey];
      return sortDirection === 'asc' ? result : -result;
    });
    return rows;
  }, [data?.pages, sortDirection, sortKey]);

  const toggleSort = (key: PageSortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortKey(key);
    setSortDirection(key === 'host' || key === 'path' ? 'asc' : 'desc');
  };

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between'>
        <div>
          <h1 className='text-2xl font-semibold text-gray-900'>Main Domains</h1>
          <p className='mt-1 text-sm text-gray-600'>
            Public web traffic for harborcoparent.com and harbordivorce.com from
            Amplitude. Bots are excluded; unique visitors are distinct Amplitude
            users or devices.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-3'>
          <label className='text-sm text-gray-600'>
            Range
            <select
              className='ml-2 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm'
              value={days}
              onChange={(event) => {
                const value = event.target.value as RangeDays;
                track('Main Domains Range Changed', { days: Number(value) });
                void navigate({ search: { days: value, host } });
              }}
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={option.days} value={option.days}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className='text-sm text-gray-600'>
            Domain
            <select
              className='ml-2 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm'
              value={host ?? ''}
              onChange={(event) => {
                const value = event.target.value || undefined;
                track('Main Domains Filter Changed', {
                  domain: value ?? 'all',
                });
                void navigate({ search: { days, host: value } });
              }}
            >
              <option value=''>All domains</option>
              {(data?.known_hosts ?? []).map((domain) => (
                <option key={domain} value={domain}>
                  {domain}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {statsQuery.isLoading && !data ? (
        <div className='flex justify-center py-16'>
          <div className='h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        </div>
      ) : null}

      {statsQuery.isError ? (
        <div className='rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800'>
          Failed to load main-domain traffic.
        </div>
      ) : null}

      {data?.error ? (
        <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
          {data.stale_at
            ? `Showing cached data from ${new Date(
                data.stale_at
              ).toLocaleString()}. ${data.error}`
            : `Amplitude traffic is unavailable: ${data.error}`}
        </div>
      ) : null}

      {data ? (
        <>
          <div className='grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6'>
            <StatCard
              label='Visits'
              value={formatNumber(data.overview.visits)}
              hint={`${daysNum}d · public sessions`}
            />
            <StatCard
              label='Unique visitors'
              value={formatNumber(data.overview.unique_visitors)}
              hint='Distinct users/devices'
            />
            <StatCard
              label='CTA clicks'
              value={formatNumber(data.overview.cta_clicks)}
              hint='Store, signup, and lead CTAs'
            />
            <StatCard
              label='CTA rate'
              value={formatPercent(data.overview.cta_rate)}
              hint='CTA clicks / visits'
            />
            <StatCard
              label='Domains'
              value={formatNumber(data.overview.domains)}
              hint={data.host ?? 'Canonical hosts'}
            />
            <StatCard
              label='Active pages'
              value={formatNumber(data.overview.pages)}
              hint='Session entry paths'
            />
          </div>

          <section className='space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
            <div className='flex flex-wrap items-baseline justify-between gap-2'>
              <h2 className='text-lg font-semibold text-gray-900'>
                Traffic trend
              </h2>
              <span className='text-xs text-gray-400'>{data.source}</span>
            </div>
            <TrendChart data={data.daily} />
          </section>

          <section className='space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
            <h2 className='text-lg font-semibold text-gray-900'>Domains</h2>
            <div className='grid gap-3 lg:grid-cols-2'>
              {data.domains.map((domain) => (
                <article
                  key={domain.host}
                  className='rounded-md border border-gray-200 p-4'
                >
                  <p className='font-mono text-sm font-semibold text-gray-900'>
                    {domain.host}
                  </p>
                  <div className='mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4'>
                    <div>
                      <p className='text-xs text-gray-500'>Visits</p>
                      <p className='font-semibold tabular-nums'>
                        {formatNumber(domain.visits)}
                      </p>
                    </div>
                    <div>
                      <p className='text-xs text-gray-500'>Uniques</p>
                      <p className='font-semibold tabular-nums'>
                        {formatNumber(domain.unique_visitors)}
                      </p>
                    </div>
                    <div>
                      <p className='text-xs text-gray-500'>CTA clicks</p>
                      <p className='font-semibold tabular-nums'>
                        {formatNumber(domain.cta_clicks)}
                      </p>
                    </div>
                    <div>
                      <p className='text-xs text-gray-500'>CTA rate</p>
                      <p className='font-semibold tabular-nums'>
                        {formatPercent(domain.cta_rate)}
                      </p>
                    </div>
                  </div>
                  <p className='mt-3 text-xs text-gray-400'>
                    Last active: {domain.last_active_date ?? '—'}
                  </p>
                </article>
              ))}
            </div>
          </section>

          <section className='space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
            <div className='flex flex-wrap items-center justify-between gap-3'>
              <h2 className='text-lg font-semibold text-gray-900'>Pages</h2>
              <button
                type='button'
                onClick={() => exportCsv(sortedPages, daysNum)}
                className='inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50'
              >
                <FiDownload className='h-4 w-4' />
                Export CSV
              </button>
            </div>
            <div className='overflow-x-auto rounded-md border border-gray-200'>
              <table className='w-full text-sm'>
                <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
                  <tr>
                    {(
                      [
                        ['host', 'Domain'],
                        ['path', 'Entry path'],
                        ['visits', 'Visits'],
                        ['unique_visitors', 'Uniques'],
                        ['cta_clicks', 'CTA clicks'],
                        ['cta_rate', 'CTA rate'],
                      ] as Array<[PageSortKey, string]>
                    ).map(([key, label]) => (
                      <th key={key} className='px-4 py-3 text-left'>
                        <button
                          type='button'
                          onClick={() => toggleSort(key)}
                          className='font-semibold uppercase tracking-wide hover:text-gray-800'
                        >
                          {label}
                          {sortKey === key
                            ? sortDirection === 'asc'
                              ? ' ↑'
                              : ' ↓'
                            : ''}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {sortedPages.length ? (
                    sortedPages.map((row) => (
                      <tr
                        key={`${row.host}:${row.path}`}
                        className='hover:bg-gray-50'
                      >
                        <td className='px-4 py-2 font-mono text-gray-700'>
                          {row.host}
                        </td>
                        <td className='px-4 py-2 font-mono text-gray-800'>
                          {row.path}
                        </td>
                        <td className='px-4 py-2 tabular-nums'>
                          {formatNumber(row.visits)}
                        </td>
                        <td className='px-4 py-2 tabular-nums'>
                          {formatNumber(row.unique_visitors)}
                        </td>
                        <td className='px-4 py-2 tabular-nums'>
                          {formatNumber(row.cta_clicks)}
                        </td>
                        <td className='px-4 py-2 tabular-nums'>
                          {formatPercent(row.cta_rate)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className='px-4 py-8 text-center text-gray-400'
                      >
                        No visits in this range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/main-domains')({
  component: AdminMainDomainsPage,
  validateSearch: (search: Record<string, unknown>) => {
    const daysRaw = typeof search.days === 'string' ? search.days : '30';
    const hostRaw = typeof search.host === 'string' ? search.host : undefined;
    return {
      days: (ALLOWED_DAYS.has(daysRaw) ? daysRaw : '30') as RangeDays,
      host: hostRaw || undefined,
    };
  },
});
